import { useState } from "react";
import { api } from "../api";
import type { User } from "../types";

/**
 * Shown once, before the first question. Acceptance is recorded server-side
 * with a timestamp.
 *
 * The point that matters is the second bullet: an advocate's duty of
 * professional secrecy runs to the client, and the client is not here to
 * waive it. Asking for abstract questions protects them and also produces
 * better evaluation data, since abstract questions generalise across the
 * corpus and case-specific ones do not.
 */
export function Consent({
  user,
  onAccept,
}: {
  user: User;
  onAccept: (user: User) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      onAccept(await api.consent());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Eroare");
      setBusy(false);
    }
  }

  return (
    <div className="centered">
      <div className="auth-card consent">
        <h2>Condițiile pilotului</h2>
        <p className="muted">
          Bine ați venit, {user.name}. Înainte de a începe, câteva lucruri pe
          care trebuie să le știți.
        </p>

        <ul className="terms">
          <li>
            <strong>Conversațiile sunt înregistrate.</strong> Întrebările,
            răspunsurile, sursele citate și evaluările dumneavoastră sunt
            stocate și analizate de echipa ArhiLex pentru a măsura și
            îmbunătăți calitatea răspunsurilor.
          </li>
          <li>
            <strong>Nu introduceți date care identifică un client.</strong>{" "}
            Secretul profesional al avocatului aparține clientului, nu
            avocatului. Formulați întrebările abstract — despre normă, nu
            despre speță.
          </li>
          <li>
            <strong>Documentele încărcate intră sub aceeași regulă.</strong>{" "}
            Puteți atașa un document sau o imagine pentru a pune o întrebare
            despre el. Fișierul se stochează pe serverele ArhiLex, textul lui
            este citit de sistem și rămâne disponibil echipei pentru
            evaluare, la fel ca întrebările. Anonimizați documentul înainte
            de a-l încărca — ștergeți numele, codurile de identificare și
            adresele. Puteți elimina oricând un fișier; ștergerea
            conversației îl elimină odată cu ea.
          </li>
          <li>
            <strong>Răspunsurile nu sunt consultanță juridică.</strong> Sistemul
            poate cita greșit sau poate folosi o versiune care nu mai este în
            vigoare. Verificați întotdeauna sursa citată înainte de a vă baza
            pe ea.
          </li>
          <li>
            <strong>Datele se șterg după evaluare.</strong> Înregistrările sunt
            păstrate doar pe durata pilotului și a analizei ulterioare, apoi
            sunt eliminate. Puteți cere ștergerea datelor dumneavoastră oricând.
          </li>
        </ul>

        <label className="checkline">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span>
            Am citit și am înțeles condițiile de mai sus.
          </span>
        </label>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button className="btn primary" onClick={accept} disabled={!checked || busy}>
          {busy ? "Se salvează…" : "Continuă"}
        </button>
      </div>
    </div>
  );
}
